// Shared helpers for the RowCentral analytics dashboards.
// Handles MM/DD/YYYY date parsing, split/time formatting, category grouping,
// and linear-regression trendlines used across athlete and coach views.

import { goalBoatClassKey, GOAL_BOAT_CLASSES, percentOfTwoKSpeed } from '../../utils/korzoChart';

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export { GOAL_BOAT_CLASSES };

// Parse a stored MM/DD/YYYY date string into epoch ms (UTC midnight).
export const parseResultDate = (value) => {
  if (!value) return null;
  const parts = String(value).trim().split('/');
  if (parts.length !== 3) {
    const fallback = Date.parse(value);
    return Number.isNaN(fallback) ? null : fallback;
  }
  const [month, day, year] = parts.map((p) => parseInt(p, 10));
  if (!month || !day || !year) return null;
  return Date.UTC(year, month - 1, day);
};

// Split (seconds per 500m): prefer stored value, otherwise compute from time/distance.
export const splitFor = (row) => {
  const stored = row.avg_split_seconds != null ? Number(row.avg_split_seconds) : null;
  if (stored != null && !Number.isNaN(stored) && stored > 0) return stored;
  const time = Number(row.total_time_seconds);
  const distance = Number(row.distance_meters);
  if (time > 0 && distance > 0) return (time / distance) * 500;
  return null;
};

export const percentOfTwoKForResult = (row) => {
  const stored = row?.speed_percent != null ? Number(row.speed_percent) : null;
  if (stored != null && Number.isFinite(stored)) return stored;
  return percentOfTwoKSpeed(splitFor(row), row?.effective_goal_2k_seconds);
};

export const twoKGoalClassForResult = (row) => {
  const key = goalBoatClassKey(row?.boat_class);
  return GOAL_BOAT_CLASSES.includes(key) ? key : null;
};

// Canonical boat class ("Men's Erg" -> "M Erg"), matching backend GMS normalization.
export const normalizeBoatClass = (boatClass) => {
  if (!boatClass) return boatClass;
  return String(boatClass)
    .replace(/^Men's\s*/i, 'M ')
    .replace(/^Women's\s*/i, 'W ')
    .replace(/\s+/g, ' ')
    .trim();
};

// % GMS = (GMS pace / athlete pace) * 100. Higher is better; 100% = at standard.
export const percentGms = (split, gmsPace500) => {
  if (split == null || !gmsPace500 || split <= 0) return null;
  return (gmsPace500 / split) * 100;
};

// GMS pace (per 500m) for a result row's boat class, or null when no standard exists.
export const gmsPaceForRow = (row, gmsMap) => {
  if (!gmsMap) return null;
  const pace = gmsMap[normalizeBoatClass(row.boat_class)];
  return pace != null && pace > 0 ? pace : null;
};

export const METRICS = {
  percentGms: { key: 'percentGms', label: '% CST', lowerIsBetter: false, format: (v) => (v == null || Number.isNaN(v) ? '-' : `${v.toFixed(1)}%`) },
  split: { key: 'split', label: 'Split (/500m)', lowerIsBetter: true, format: (v) => formatSplit(v) },
  time: { key: 'time', label: 'Total time', lowerIsBetter: true, format: (v) => formatDuration(v) },
  strokeRate: { key: 'strokeRate', label: 'Stroke rate (spm)', lowerIsBetter: false, format: (v) => (v == null ? '-' : v.toFixed(1)) }
};

export const DEFAULT_METRIC = 'percentGms';

// Numeric value for a result row given the selected metric.
// `gmsMap` ({ canonicalBoatClass: pace500 }) is required for the percentGms metric.
export const metricValue = (row, metric, gmsMap = null) => {
  if (metric === 'percentGms') {
    return percentGms(splitFor(row), gmsPaceForRow(row, gmsMap));
  }
  if (metric === 'time') {
    const t = Number(row.total_time_seconds);
    return t > 0 ? t : null;
  }
  if (metric === 'strokeRate') {
    const r = row.avg_stroke_rate != null ? Number(row.avg_stroke_rate) : null;
    return r != null && !Number.isNaN(r) && r > 0 ? r : null;
  }
  return splitFor(row);
};

// Category key: comparable group = boat class + distance + result type.
export const categoryKey = (row) => {
  const boat = row.boat_class || 'Unknown';
  const dist = row.distance_meters != null ? `${row.distance_meters}m` : 'time';
  const type = row.result_type || 'workout';
  return `${boat} \u00b7 ${dist} \u00b7 ${type}`;
};

// Group result rows into detected categories, each with a sorted point series.
// When metric is percentGms and a category's boat class has no GMS, the
// category falls back to `split` and is marked `usedFallbackMetric`.
export const groupByCategory = (rows, metric, gmsMap = null) => {
  const map = new Map();
  for (const row of rows) {
    const ms = parseResultDate(row.result_date);
    if (ms == null) continue;

    let effectiveMetric = metric;
    let value = metricValue(row, metric, gmsMap);
    if (value == null && metric === 'percentGms' && gmsPaceForRow(row, gmsMap) == null) {
      effectiveMetric = 'split';
      value = metricValue(row, 'split');
    }
    if (value == null) continue;

    const key = categoryKey(row);
    if (!map.has(key)) {
      map.set(key, {
        key,
        boatClass: row.boat_class || 'Unknown',
        distanceMeters: row.distance_meters != null ? Number(row.distance_meters) : null,
        resultType: row.result_type || 'workout',
        metric: effectiveMetric,
        usedFallbackMetric: effectiveMetric !== metric,
        points: []
      });
    }
    map.get(key).points.push({ dateMs: ms, date: row.result_date, value, row });
  }
  const categories = [...map.values()];
  categories.forEach((c) => c.points.sort((a, b) => a.dateMs - b.dateMs));
  categories.sort((a, b) => b.points.length - a.points.length);
  return categories;
};

// Least-squares slope of y over x. Returns null when insufficient data.
export const linearSlope = (points) => {
  if (!points || points.length < 2) return null;
  const n = points.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (const { x, y } of points) {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
};

// Coefficient of determination (R²) for a linear fit — how well the trendline
// explains the variance in y. 0-1, higher = tighter fit around the line.
export const rSquared = (points, fit) => {
  if (!points || points.length < 2 || !fit) return null;
  const meanY = points.reduce((s, p) => s + p.y, 0) / points.length;
  let ssTot = 0;
  let ssRes = 0;
  for (const { x, y } of points) {
    const predicted = fit.intercept + fit.slope * x;
    ssRes += (y - predicted) ** 2;
    ssTot += (y - meanY) ** 2;
  }
  if (ssTot === 0) return null;
  return 1 - ssRes / ssTot;
};

// Build a two-point trendline (in ms/value space) for a set of points, plus R².
export const trendlineFor = (points) => {
  if (!points || points.length < 2) return null;
  const firstMs = points[0].dateMs;
  const regressionPoints = points.map((p) => ({ x: (p.dateMs - firstMs) / MS_PER_DAY, y: p.value }));
  const fit = linearSlope(regressionPoints);
  if (!fit) return null;
  const lastMs = points[points.length - 1].dateMs;
  const startDays = 0;
  const endDays = (lastMs - firstMs) / MS_PER_DAY;
  return {
    slopePerDay: fit.slope,
    slopePer30Days: fit.slope * 30,
    r2: rSquared(regressionPoints, fit),
    start: { dateMs: firstMs, value: fit.intercept + fit.slope * startDays },
    end: { dateMs: lastMs, value: fit.intercept + fit.slope * endDays }
  };
};

// ---- Water vs. Erg classification + distance bucketing ----

// Boat classes containing "Erg" (e.g. "M Erg", "W Erg") are ergometer results;
// everything else (1x, 2x, 4+, 8+, ...) is on-water.
export const isErgBoatClass = (boatClass) => /erg/i.test(String(boatClass || ''));

export const DISTANCE_BUCKETS = [
  { key: 'lt500', label: '<500m', min: 0, max: 499 },
  { key: '500to1250', label: '500-1250m', min: 500, max: 1250 },
  { key: '1250to2000', label: '1250-2000m', min: 1251, max: 2000 },
  { key: 'gt2000', label: '>2000m', min: 2001, max: Infinity }
];

// Which distance bucket a piece's distance_meters falls into, or null when
// there is no distance (pure time-based workouts with no meters logged).
export const bucketForDistance = (distanceMeters) => {
  const d = Number(distanceMeters);
  if (!d || Number.isNaN(d) || d <= 0) return null;
  return DISTANCE_BUCKETS.find((b) => d >= b.min && d <= b.max) || null;
};

const parseIntervalDetails = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Split from an interval's own time and distance.
export const splitForInterval = (timeSeconds, distanceMeters) => {
  const time = Number(timeSeconds);
  const distance = Number(distanceMeters);
  if (time > 0 && distance > 0) return (time / distance) * 500;
  return null;
};

// Result-type and time-window filters for the CST gauge / team compare.
// Labels match athlete-results tags (workout / race / test). Total = all types.
export const CST_SCOPES = [
  { key: 'total', label: 'Total', types: null },
  { key: 'workout', label: 'Workout', types: ['workout'] },
  { key: 'race', label: 'Race', types: ['race'] },
  { key: 'test', label: 'Training', types: ['test'] }
];

export const CST_WINDOWS = [
  { key: '3', label: '3 months', months: 3 },
  { key: '6', label: '6 months', months: 6 },
  { key: '12', label: '12 months', months: 12 },
  { key: 'all', label: 'All time', months: null }
];

export const cstWindowCutoffMs = (windowKey, now = Date.now()) => {
  const months = CST_WINDOWS.find((w) => w.key === windowKey)?.months;
  if (!months) return null;
  const d = new Date(now);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.getTime();
};

export const resultMatchesCstFilter = (row, { scope = 'total', windowKey = 'all', now = Date.now() } = {}) => {
  const scopeDef = CST_SCOPES.find((s) => s.key === scope);
  const types = scopeDef?.types;
  const resultType = row?.result_type || 'workout';
  if (types && !types.includes(resultType)) return false;
  const cutoff = cstWindowCutoffMs(windowKey, now);
  if (cutoff == null) return true;
  const ms = parseResultDate(row?.result_date);
  return ms != null && ms >= cutoff;
};

export const cstFilterLabel = (scope, windowKey) => {
  const scopeLabel = CST_SCOPES.find((s) => s.key === scope)?.label || 'Total';
  const windowLabel = CST_WINDOWS.find((w) => w.key === windowKey)?.label || 'All time';
  return `${scopeLabel} · ${windowLabel}`;
};

/**
 * Expand a result into interval pieces for distance-bucketed charts.
 * A 4x500m workout becomes pieces at 500m (not one 2000m total). Variable
 * pyramids keep each piece's own meters. Workout-level CST is used only when
 * an interval has no time of its own.
 */
export const intervalPiecesForResult = (row) => {
  const details = parseIntervalDetails(row?.interval_details);
  const totalDistance = Number(row?.distance_meters);
  const intervalCount = Math.max(1, parseInt(row?.interval_count, 10) || 1);
  const uniformPieceDistance =
    totalDistance > 0 && intervalCount > 0 ? totalDistance / intervalCount : null;
  const workoutSplit = splitFor(row);

  const pieceFrom = (distanceMeters, timeSeconds) => {
    const distance = Number(distanceMeters);
    if (!distance || Number.isNaN(distance) || distance <= 0) return null;
    const split = splitForInterval(timeSeconds, distance) ?? workoutSplit;
    if (split == null) return null;
    return { distanceMeters: distance, splitSeconds: split };
  };

  if (details.length > 0) {
    const pieces = details
      .map((interval) =>
        pieceFrom(interval?.distance_meters || uniformPieceDistance, interval?.time_seconds)
      )
      .filter(Boolean);
    if (pieces.length > 0) return pieces;
  }

  const distance = intervalCount > 1 ? uniformPieceDistance : totalDistance;
  // Without per-interval times, keep the workout split rather than treating
  // total time as if it were one piece's time.
  const single = pieceFrom(distance, intervalCount > 1 ? null : row?.total_time_seconds);
  return single ? [single] : [];
};

// Simple arithmetic mean, or null for an empty list.
export const average = (values) => {
  if (!values || values.length === 0) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
};

// The personal-best point for a series given the metric's direction.
export const personalBest = (points, metric) => {
  if (!points || points.length === 0) return null;
  const lowerIsBetter = METRICS[metric]?.lowerIsBetter !== false;
  return points.reduce((best, p) =>
    (lowerIsBetter ? p.value < best.value : p.value > best.value) ? p : best
  );
};

// Flag points more than `stdDevs` standard deviations from the series mean.
// Returns a Set of dateMs keys for the flagged points.
export const flagOutliers = (points, stdDevs = 3) => {
  const flagged = new Set();
  if (!points || points.length < 4) return flagged;
  const values = points.map((p) => p.value);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  const std = Math.sqrt(variance);
  if (std === 0) return flagged;
  points.forEach((p) => {
    if (Math.abs(p.value - mean) > stdDevs * std) flagged.add(p.dateMs);
  });
  return flagged;
};

// Percentage improvement from first to last point (positive = got faster for split/time).
export const improvementPct = (points, metric) => {
  if (!points || points.length < 2) return null;
  const first = points[0].value;
  const last = points[points.length - 1].value;
  if (first === 0) return null;
  const lowerIsBetter = METRICS[metric]?.lowerIsBetter !== false;
  const raw = ((first - last) / first) * 100;
  return lowerIsBetter ? raw : -raw;
};

// ---- Formatting ----

// Format seconds as m:ss.d (used for split and short durations).
export const formatSplit = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
};

// Format seconds as h:mm:ss / m:ss for longer total times.
export const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '-';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const formatMetric = (value, metric) => {
  const def = METRICS[metric] || METRICS.split;
  return def.format(value);
};

// Short label for a chart axis tick from epoch ms.
export const formatDateShort = (ms) => {
  if (ms == null) return '';
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// A stable, theme-agnostic palette for multi-series charts.
export const SERIES_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5'
];

export const colorForIndex = (i) => SERIES_COLORS[i % SERIES_COLORS.length];
